import {
  INSURED_TYPES,
  useJointInsuredStore,
  usePrimaryInsuredStore,
} from "#src/stores/insured.js";
import {
  CONTINGENT_BENEFICIARY,
  OWNER,
  PRIMARY_BENEFICIARY,
} from "#src/structures/Role.js";
import { useEappStore } from "#src/stores/electronic-application.js";

import { QuotingPrefillService } from "#src/services/quoting-prefill.service.js";
import { InstancePrefillService } from "#src/services/instance-prefill.service.js";
import { EappPrefillService } from "#src/services/eapp-prefill.service.js";
import { FlowPrefillService } from "#src/services/flow-prefill.service.js";
import { InsuredPrefillService } from "#src/services/insured-prefill.service.js";
import { InsuredOccupationPrefillService } from "#src/services/insured-occupation-prefill.service.js";
import { IndividualPrefillService } from "#src/services/individual-prefill.service.js";
import { EntityPrefillService } from "#src/services/entity-prefill.service.js";
import { parseErrorMessage } from "#src/util/helpers.js";
import {
  enforceStrictParameterValidation,
  validatePartyData,
} from "#src/structures/quote-params/QuoteParameterValidation.js";

export class PrefillService {
  #pinia;
  #quotingPrefillService;
  #instancePrefillService;
  #eappPrefillService;
  #flowPrefillService;
  #insuredPrefillService;
  #insuredOccupationPrefillService;
  #individualPrefillService;
  #entityPrefillService;

  static VALID_PARTY_TYPES = ["individual", "business", "trust"];

  constructor(pinia) {
    this.#pinia = pinia;
    this.#quotingPrefillService = new QuotingPrefillService(pinia);
    this.#instancePrefillService = new InstancePrefillService(pinia);
    this.#eappPrefillService = new EappPrefillService(pinia);
    this.#flowPrefillService = new FlowPrefillService(pinia);
    this.#insuredPrefillService = new InsuredPrefillService(pinia);
    this.#insuredOccupationPrefillService = new InsuredOccupationPrefillService(
      pinia
    );
    this.#individualPrefillService = new IndividualPrefillService(pinia);
    this.#entityPrefillService = new EntityPrefillService(pinia);
  }

  async preCreatePrefill(
    params,
    {
      strictProductParams = false,
      strictInsuredParams = false,
      canChangeStep = false,
    } = {}
  ) {
    const insuredParams = params?.insured;
    const jointParams = params?.joint_insured;

    if (insuredParams) {
      await this.#preCreateInsuredPrefill(
        INSURED_TYPES.INSURED,
        insuredParams,
        {
          strictInsuredParams,
          includingJoint: Boolean(jointParams),
        }
      );
    }

    if (jointParams) {
      await this.#preCreateInsuredPrefill(INSURED_TYPES.JOINT, jointParams, {
        strictInsuredParams,
        includingJoint: true,
      });
    }

    this.#quotingPrefillService.setRequestPreferencesFromPrefill(params);

    if (strictProductParams)
      this.#quotingPrefillService.setStrictProductPrefill(params);
    else this.#quotingPrefillService.setPrefillWithFallback(params);

    this.preCreateEnvironmentPrefill(params, { includeQuoteData: true });
    if (canChangeStep)
      this.#flowPrefillService.setFromPrefill(params, { preCreate: true });
  }

  async preCreateEnvironmentPrefill(params, { includeQuoteData = false } = {}) {
    this.#eappPrefillService.setFromPrefill(params, { includeQuoteData });
    this.#instancePrefillService.setFromPrefill(params, { includeQuoteData });
  }

  async #preCreateInsuredPrefill(
    insuredType,
    params,
    { strictInsuredParams, includingJoint } = {}
  ) {
    await this.#insuredPrefillService.setFromPrefill(insuredType, params, {
      isPreCreate: true,
    });

    let errorKey = "insured";
    if (insuredType === INSURED_TYPES.JOINT) errorKey = "joint_insured";
    if (strictInsuredParams) {
      const validationItems = [
        "first_name",
        "last_name",
        "health",
        "birthdate",
        "gender",
        "smoker",
        "smoking_statuses",
      ];

      if (includingJoint) {
        validationItems.push("marital_status");
      }

      enforceStrictParameterValidation(
        validatePartyData(this.#pinia, INSURED_TYPES.INSURED, {
          validationItems,
          errorKey,
        })
      );
    }
  }

  async postCreatePrefill(
    params,
    { skipSetInsuredValues = false, canChangeStep = false }
  ) {
    let messages = {};

    if (params?.insured) {
      messages = {
        ...messages,
        ...(await this.#postCreateInsuredPrefill(
          INSURED_TYPES.INSURED,
          params.insured,
          {
            skipSetValues: skipSetInsuredValues,
          }
        )),
        ...(await this.#postCreateInsuredOccupationPrefill(
          INSURED_TYPES.INSURED,
          params.insured
        )),
      };
    }

    if (params?.joint_insured) {
      const jointParams = params?.joint_insured;

      messages = {
        ...messages,
        ...(await this.#postCreateInsuredPrefill(
          INSURED_TYPES.JOINT,
          jointParams,
          {
            skipSetValues: skipSetInsuredValues,
          }
        )),
        ...(await this.#postCreateInsuredOccupationPrefill(
          INSURED_TYPES.JOINT,
          jointParams
        )),
      };
    }

    messages = {
      ...messages,
      ...(await this.#postCreatePartiesPrefill(params)),
    };

    if (canChangeStep) {
      messages = {
        ...messages,
        ...(await this.#postCreateFlowPrefill(params)),
      };
    }

    if (Object.keys(messages).length) throw messages;

    return messages;
  }

  async #postCreateFlowPrefill(params) {
    let messages = {};

    if ("step" in params) {
      try {
        this.#flowPrefillService.setFromPrefill(params);
        await this.#flowPrefillService.savePrefill(params);
      } catch (e) {
        messages = e;
      }
    }

    return messages;
  }

  async #postCreateInsuredPrefill(insuredType, params, { skipSetValues }) {
    const messages = {};
    let errorKey = "insured";
    if (insuredType === INSURED_TYPES.JOINT) errorKey = "joint_insured";
    try {
      // set from prefill does a full overwrite
      // client side we want a set untouched from prefill
      const attrs = await this.#insuredPrefillService.setFromPrefill(
        insuredType,
        params,
        {
          skipSetValues,
        }
      );
      if (attrs) {
        await this.#insuredPrefillService.saveData(insuredType, attrs.valid);
        if (attrs.invalid) {
          Object.keys(attrs.invalid).forEach(key => {
            messages[`${errorKey}.${key}`] = attrs.invalid[key];
          });
        }
      }
    } catch (e) {
      messages[errorKey] = [parseErrorMessage(e)];
    }
    return messages;
  }

  async #postCreateInsuredOccupationPrefill(insuredType, params) {
    let messages = {};
    let errorKey = "insured";
    if (insuredType === INSURED_TYPES.JOINT) errorKey = "joint_insured";
    try {
      const attrs = this.#insuredOccupationPrefillService.setFromPrefill(
        insuredType,
        params
      );

      if (attrs) {
        await this.#insuredOccupationPrefillService.savePrefill(
          insuredType,
          attrs.valid
        );

        if (attrs.invalid) {
          Object.keys(attrs.invalid).forEach(key => {
            messages[`${errorKey}.occupation.${key}`] = attrs.invalid[key];
          });
        }
      }
    } catch (e) {
      messages[`${errorKey}.occupation`] = [parseErrorMessage(e)];
    }

    return messages;
  }

  async #postCreatePartiesPrefill(params) {
    let messages = {};
    let parties = params?.parties;
    if (typeof parties === "string") {
      try {
        parties = JSON.parse(parties);
        // eslint-disable-next-line no-unused-vars
      } catch (e) {
        messages.parties = ["Unable to parse"];
      }
    }

    if (Array.isArray(parties) && parties.length) {
      if (parties.some(p => p.is_owner)) {
        const insured = usePrimaryInsuredStore(this.#pinia);
        try {
          await insured.deleteRole(OWNER);
        } catch (e) {
          messages["insured.owner"] = [parseErrorMessage(e)];
        }
      }

      parties = parties.filter(Boolean);

      let index = 0;
      for (const party of parties) {
        try {
          await this.#prefillParty(party);
        } catch (e) {
          messages[`parties.${index}`] = [parseErrorMessage(e)];
        }
        ++index;
      }
    }
    return messages;
  }

  async #prefillParty(party) {
    const insured = usePrimaryInsuredStore(this.#pinia);
    const joint = useJointInsuredStore(this.#pinia);
    const eApp = useEappStore(this.#pinia);

    const partyType = party.type.toLowerCase();
    const roles = [];
    if (party.is_owner) {
      roles.push(OWNER);
    }
    if (party.is_primary_beneficiary) {
      roles.push(PRIMARY_BENEFICIARY);
    } else if (party.is_contingent_beneficiary) {
      roles.push(CONTINGENT_BENEFICIARY);
    }

    if (!PrefillService.VALID_PARTY_TYPES.includes(partyType)) {
      throw "Invalid party type";
    }
    if (!roles.length) {
      throw "Missing roles";
    }

    const insured_id = party.is_joint_insureds_beneficiary
      ? joint.id
      : insured.id;

    if (!insured_id) throw "Related insured not found";

    const partyKey = await eApp.createParty({
      type: partyType,
      role: roles,
      insured_id,
    });

    if (partyType === "individual") {
      return this.#individualPrefillService.savePrefill(partyKey, party);
    }

    return this.#entityPrefillService.savePrefill(partyKey, party);
  }
}
